import type { Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

/**
 * Fixed height for every rail item (40px = 5 spacing units). Items use this instead of
 * vertical padding so they stay at the exact same height/position whether the rail is
 * expanded (icon + label + chevron) or collapsed (icon only) — content is centered, not
 * padded, so differing content never shifts the height.
 */
export const NAV_ITEM_HEIGHT = 40

export const RAIL_WIDTH_TRANSITION_MS = 300
export const LABEL_FADE_MS = 150

export const NAV_LIST_PX = 1.25
export const NAV_ITEM_PX = 1.5
export const NAV_ICON_SIZE = 20

export const labelFadeTransition = (isExpanded: boolean): string =>
  `opacity ${LABEL_FADE_MS}ms ease-in-out ${isExpanded ? RAIL_WIDTH_TRANSITION_MS : 0}ms`

export const navLabelSx = (isExpanded: boolean): SystemStyleObject<Theme> => ({
  minWidth: 0,
  opacity: isExpanded ? 1 : 0,
  pointerEvents: isExpanded ? 'auto' : 'none',
  transition: labelFadeTransition(isExpanded),
})

export const navHiddenControlSx = (isExpanded: boolean): SystemStyleObject<Theme> => ({
  ...navLabelSx(isExpanded),
  visibility: isExpanded ? 'visible' : 'hidden',
  transition: `${labelFadeTransition(isExpanded)}, visibility ${LABEL_FADE_MS}ms ${
    isExpanded ? RAIL_WIDTH_TRANSITION_MS : 0
  }ms`,
})

export const railWidthTransition = (isExpanded: boolean): string => {
  const timing = `${RAIL_WIDTH_TRANSITION_MS}ms ease-in-out ${isExpanded ? 0 : LABEL_FADE_MS}ms`
  return `width ${timing}, min-width ${timing}`
}

/**
 * Strips the native <button> chrome (border, background, font, default width) so a
 * real button can be used for semantics + keyboard support while the visual styling
 * comes entirely from the menu-item sx that follows it in the sx array.
 *
 * Always place this FIRST in an sx array so the item's own color/background win:
 *   sx={[buttonResetSx, menuItemSx(...)]}
 */
export const buttonResetSx: SystemStyleObject<Theme> = {
  appearance: 'none',
  border: 0,
  m: 0,
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  width: '100%',
}
